/**
 * 対象日（JST 暦日）まわりの計算。
 *
 * ADR-011: タイムゾーンは Asia/Tokyo 固定。端末 TZ に依存させない。
 * JST は年間を通じて UTC+9（サマータイムなし）なので、固定オフセット計算で足りる。
 */

import type { JstDate } from './types';

/** JST のオフセット（ミリ秒）。 */
const JST_OFFSET_MS = 9 * 60 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;

const JST_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** 'YYYY-MM-DD' 形式で、かつ実在する日付か。 */
export function isValidJstDate(value: unknown): value is JstDate {
  if (typeof value !== 'string' || !JST_DATE_RE.test(value)) return false;
  // 2026-02-30 のような存在しない日を弾く（Date は繰り上げてしまうため往復比較する）。
  const ms = Date.parse(`${value}T00:00:00Z`);
  if (Number.isNaN(ms)) return false;
  return new Date(ms).toISOString().slice(0, 10) === value;
}

/** ある時刻における JST の暦日を返す。 */
export function toJstDate(at: Date | number = Date.now()): JstDate {
  const ms = typeof at === 'number' ? at : at.getTime();
  return new Date(ms + JST_OFFSET_MS).toISOString().slice(0, 10);
}

/** JST の「今日」。新規ノートの対象日の初期値。 */
export function todayJst(now: Date | number = Date.now()): JstDate {
  return toJstDate(now);
}

/** JST の暦日を days 日ずらす。負値で過去方向。 */
export function addDays(date: JstDate, days: number): JstDate {
  const ms = Date.parse(`${date}T00:00:00Z`);
  if (Number.isNaN(ms)) throw new RangeError(`不正な対象日: ${date}`);
  return new Date(ms + days * DAY_MS).toISOString().slice(0, 10);
}

/** JST の「昨日」。深夜に前日分を書くための動線に使う。 */
export function yesterdayJst(now: Date | number = Date.now()): JstDate {
  return addDays(todayJst(now), -1);
}

/** a から b までの日数（b - a）。同日なら 0。 */
export function daysBetween(a: JstDate, b: JstDate): number {
  const ma = Date.parse(`${a}T00:00:00Z`);
  const mb = Date.parse(`${b}T00:00:00Z`);
  if (Number.isNaN(ma) || Number.isNaN(mb)) {
    throw new RangeError(`不正な対象日: ${a} / ${b}`);
  }
  return Math.round((mb - ma) / DAY_MS);
}

/**
 * タイムラインの既定の開閉（docs/01-requirements.md §5.6）。
 * 対象日が直近 3 日以内なら展開、それ以前は畳む。
 *
 * 期間ノートは「期間の終了日」が直近なら展開する（進行中の期間を畳まないため）。
 */
export function isRecentForTimeline(
  subjectDate: JstDate,
  subjectDateEnd: JstDate | null,
  now: Date | number = Date.now(),
): boolean {
  const today = todayJst(now);
  const effective = subjectDateEnd && subjectDateEnd > subjectDate ? subjectDateEnd : subjectDate;
  const diff = daysBetween(effective, today);
  // 未来日（diff < 0）も直近扱いにする。
  return diff <= 2;
}

/**
 * 対象日・対象期間の整合性検査。
 * 終了日は開始日以降でなければならない。同日なら「単日」として終了日を落とす。
 */
export function normalizeSubjectRange(
  subjectDate: JstDate,
  subjectDateEnd: JstDate | null | undefined,
): { subject_date: JstDate; subject_date_end: JstDate | null } {
  if (!isValidJstDate(subjectDate)) {
    throw new RangeError(`不正な対象日: ${subjectDate}`);
  }
  if (subjectDateEnd == null || subjectDateEnd === '') {
    return { subject_date: subjectDate, subject_date_end: null };
  }
  if (!isValidJstDate(subjectDateEnd)) {
    throw new RangeError(`不正な対象期間の終了日: ${subjectDateEnd}`);
  }
  if (subjectDateEnd < subjectDate) {
    throw new RangeError(`対象期間の終了日が開始日より前です: ${subjectDate} 〜 ${subjectDateEnd}`);
  }
  if (subjectDateEnd === subjectDate) {
    return { subject_date: subjectDate, subject_date_end: null };
  }
  return { subject_date: subjectDate, subject_date_end: subjectDateEnd };
}

/** 一覧表示用の対象日ラベル（例: '2026-07-27' → '2026年7月27日(月)'）。 */
export function formatJstDateLabel(date: JstDate): string {
  const ms = Date.parse(`${date}T00:00:00Z`);
  if (Number.isNaN(ms)) return date;
  const d = new Date(ms);
  const weekday = ['日', '月', '火', '水', '木', '金', '土'][d.getUTCDay()]!;
  return `${d.getUTCFullYear()}年${d.getUTCMonth() + 1}月${d.getUTCDate()}日(${weekday})`;
}
